import { App } from '@capacitor/app';
import { PushNotifications, type Token } from '@capacitor/push-notifications';
import { isNative, platform } from '../platform';
import { supabase } from '../supabase';
import posthog from '../posthog';

type PushHandlers = {
  onReceived?: (title: string, body: string, data: Record<string, unknown>) => void;
  onActionTap?: (data: Record<string, unknown>) => void;
};

/**
 * Report each step of push registration to analytics (never the token itself),
 * so a phone that can't register shows exactly where it stops.
 */
function report(stage: string, props: Record<string, unknown> = {}) {
  try {
    posthog.capture('push_registration', { stage, platform, ...props });
  } catch {
    // Analytics must never get in the way of registering.
  }
}

/**
 * Register the device for push notifications and persist the resulting token
 * against the signed-in user so the backend can target them. No-op on web.
 * Returns a cleanup function that removes the listeners.
 */
export async function registerPush(handlers: PushHandlers = {}): Promise<() => void> {
  if (!isNative) return () => {};

  const perm = await PushNotifications.checkPermissions();
  let receive = perm.receive;
  if (receive === 'prompt' || receive === 'prompt-with-rationale') {
    receive = (await PushNotifications.requestPermissions()).receive;
  }
  report('permission', { result: receive });
  if (receive !== 'granted') return () => {};

  const handles = [
    await PushNotifications.addListener('registration', (token: Token) => {
      report('token_received');
      void savePushToken(token.value);
    }),
    await PushNotifications.addListener('registrationError', (err) => {
      console.error('Push registration error', err);
      report('registration_error', { error: String((err as { error?: unknown }).error ?? err).slice(0, 300) });
    }),
    await PushNotifications.addListener('pushNotificationReceived', (notif) => {
      handlers.onReceived?.(notif.title ?? '', notif.body ?? '', notif.data ?? {});
    }),
    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      handlers.onActionTap?.(action.notification.data ?? {});
    }),
  ];

  report('registering');
  await PushNotifications.register();

  return () => handles.forEach((h) => void h.remove());
}

/** Where this phone remembers the token it last gave the signed-in account. */
const TOKEN_KEY = 'sekretly_push_token';

function rememberToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Storage can be unavailable; the next sign-in claims the token again anyway.
  }
}

function rememberedToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Hand this phone's token to the signed-in user. A phone is one token, and it can
 * be used by several accounts in turn, so the server moves it to whoever is
 * signed in now (claim_push_token) instead of the app writing the row itself,
 * which the table's row security refuses once another account holds the token.
 */
async function savePushToken(token: string): Promise<void> {
  const { data } = await supabase.auth.getUser();
  const userId = data.user?.id;
  if (!userId) {
    report('save_skipped_no_user');
    return;
  }

  // The bundle id tells the server which app (traveler or host) this token is for, and the
  // version lets an admin's "new version is out" push skip phones that already updated.
  let appId: string | null = null;
  let appVersion: string | null = null;
  try {
    const info = await App.getInfo();
    appId = info.id;
    appVersion = info.version || null;
  } catch {
    // Older shells without App.getInfo: the server falls back to the traveler app, and no version.
  }

  const { error } = await supabase.rpc('claim_push_token', { p_token: token, p_platform: platform, p_app_id: appId, p_app_version: appVersion });
  if (error) {
    console.error('Saving push token failed', error);
    report('save_error', { error: error.message.slice(0, 300), code: error.code });
  } else {
    rememberToken(token);
    report('saved', { app_id: appId });
  }
}

/**
 * Stop this phone receiving the signed-in account's pushes. Call it BEFORE
 * signing out (afterwards there is no session to authorize it). Never throws and
 * never holds up sign-out for long: if it can't finish, the next account to sign
 * in on the phone takes the token over anyway.
 */
export async function releasePushToken(): Promise<void> {
  if (!isNative) return;
  const token = rememberedToken();
  if (!token) return;
  try {
    const result = await Promise.race([
      supabase.rpc('release_push_token', { p_token: token }),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000)),
    ]);
    if (result && !result.error) {
      rememberToken(null);
      report('released');
    } else {
      report('release_failed', { error: result?.error?.message?.slice(0, 300) ?? 'timeout' });
    }
  } catch (err) {
    report('release_failed', { error: String(err).slice(0, 300) });
  }
}
